import type { AccountView, InvitationEmailOutcome } from "@kwp/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { Button, ErrorText, Section, TextField } from "../../ui";
import { useCurrentWorkspaceId } from "../shell/current-workspace";
import { PROVIDER_LABEL } from "./accounts-model";
import { useMe, type MyWorkspace } from "../../lib/me";
import { defaultInviteWorkspace, defaultSender, invitableWorkspaces, type InviteResult } from "./invite-model";
import { OutcomeLine, useInvitationSender, type InvitationSender } from "./invite-sender";
import { PendingInvitations } from "./PendingInvitations";

/**
 * Shown to admins only (the api enforces it too): invite someone into a shared workspace the
 * admin owns so their first sign-in enrols them there (WP-028; someone already signed in is
 * added at once), and (WP-027) email them the invitation from one of the admin's own mailboxes. Emailing
 * is a `send` action: with the passkey confirmed it goes at once, otherwise it waits in Approvals.
 */
export function InvitePersonForm() {
  const trpc = useTRPC();
  const accounts = useQuery(trpc.accounts.list.queryOptions());
  const workspaceId = useCurrentWorkspaceId();
  const workspace = useQuery({ ...trpc.workspaces.get.queryOptions({ workspaceId: workspaceId === null ? "" : workspaceId }), enabled: workspaceId !== null });
  const list: readonly AccountView[] = accounts.data === undefined ? [] : accounts.data;
  const fallback = defaultSender(list, workspace.data === undefined ? null : workspace.data.defaultFromAccountId);
  const [chosen, setChosen] = useState<string | null>(null);
  const sendFrom = chosen !== null && list.some((a) => a.id === chosen) ? chosen : fallback;
  const send = useInvitationSender();
  const invitable = invitableWorkspaces(useMe().workspaces);

  return (
    <>
      {invitable.length === 0 ? (
        <Section title="Invite a person" description="Invitations name a shared workspace the person joins.">
          <p className="text-sm text-muted">Create a shared workspace first (above); then you can invite people into it.</p>
        </Section>
      ) : (
        <InviteSection
          workspaces={invitable}
          initialWorkspaceId={defaultInviteWorkspace(invitable, workspaceId)}
          accounts={list}
          loading={accounts.isPending}
          sendFrom={sendFrom}
          onSendFrom={setChosen}
          send={send}
        />
      )}
      <PendingInvitations sendFrom={sendFrom} send={send} />
    </>
  );
}

function InviteSection({
  workspaces,
  initialWorkspaceId,
  accounts,
  loading,
  sendFrom,
  onSendFrom,
  send,
}: {
  workspaces: readonly MyWorkspace[];
  initialWorkspaceId: string | null;
  accounts: readonly AccountView[];
  loading: boolean;
  sendFrom: string | null;
  onSendFrom: (id: string) => void;
  send: InvitationSender;
}) {
  const id = useId();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [emailIt, setEmailIt] = useState(true);
  const [picked, setPicked] = useState(initialWorkspaceId);
  const target = picked !== null && workspaces.some((w) => w.id === picked) ? picked : initialWorkspaceId;
  const [done, setDone] = useState<{ email: string; outcome: InvitationEmailOutcome; invited: InviteResult | null } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const canEmail = sendFrom !== null;
  const sendingFrom = emailIt && canEmail ? sendFrom : null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setDone(null);
    setFailure(null);
    const invited = email.trim();
    if (target === null) return;
    try {
      const out = await send.invite({ email: invited, name: name.trim(), workspaceId: target, sendFromAccountId: sendingFrom });
      if (out === "cancelled") {
        setFailure("Not invited: confirm with your passkey to email the invitation, or untick “Send invitation email”.");
        return;
      }
      setDone({ email: out.email, outcome: out.emailOutcome, invited: { workspaceName: out.workspaceName, added: out.added } });
      setEmail("");
      setName("");
    } catch (err) {
      setFailure(errorMessage(err));
    }
  }

  async function retry(to: string, from: string) {
    setFailure(null);
    try {
      const outcome = await send.resend(to, from);
      if (outcome === "cancelled") setFailure("Not sent: confirm with your passkey to email the invitation.");
      else setDone({ email: to, outcome, invited: null });
    } catch (err) {
      setFailure(errorMessage(err));
    }
  }

  return (
    <Section
      title="Invite a person"
      description="They can sign in with Google once invited and join the workspace you choose on their first sign-in. Someone who has already signed in is added at once."
    >
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-workspace`} className="text-sm font-medium text-text">
            Invite to workspace
          </label>
          <select
            id={`${id}-workspace`}
            required
            value={target === null ? "" : target}
            onChange={(e) => setPicked(e.target.value)}
            className="min-h-9 w-full max-w-md rounded-md border border-border bg-surface px-2 text-sm text-text"
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField label="Email" type="email" name="email" autoComplete="off" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField label="Name" name="name" autoComplete="off" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-text">
          <input type="checkbox" checked={emailIt && canEmail} disabled={!canEmail} onChange={(e) => setEmailIt(e.target.checked)} />
          Send invitation email
        </label>
        {canEmail ? (
          <div className="flex flex-col gap-1">
            <label htmlFor={`${id}-from`} className="text-sm font-medium text-text">
              Send the invitation from
            </label>
            <select
              id={`${id}-from`}
              value={sendFrom}
              disabled={!emailIt}
              onChange={(e) => onSendFrom(e.target.value)}
              className="min-h-9 w-full max-w-md rounded-md border border-border bg-surface px-2 text-sm text-text"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.address} ({PROVIDER_LABEL[a.provider]})
                </option>
              ))}
            </select>
          </div>
        ) : loading ? null : (
          <p className="text-sm text-muted">
            To email the invitation, connect a mailbox in{" "}
            <Link to="/settings/accounts" className="text-accent underline-offset-2 hover:underline">
              Mail accounts
            </Link>
            .
          </p>
        )}
        <div>
          <Button type="submit" variant="primary" disabled={send.pending}>
            {send.pending ? "Inviting…" : "Invite"}
          </Button>
        </div>
      </form>
      <div className="mt-2 min-h-5">
        {failure === null ? null : <ErrorText>{failure}</ErrorText>}
        {done === null ? null : (
          <OutcomeLine
            email={done.email}
            outcome={done.outcome}
            invited={done.invited}
            busy={send.pending}
            onResend={sendFrom === null || (done.invited !== null && done.invited.added) ? null : () => void retry(done.email, sendFrom)}
          />
        )}
      </div>
    </Section>
  );
}
